import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import {
  FormControl,
  Grid,
  FormLabel,
  Skeleton,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { useSubscriptionFlowConfirmation } from "@queries/subscription-flow/useSubscriptionFlowConfirmation";
import { PaymentMethodsIcon } from "@assets/icons/PaymentMethodsIcon";
import { FormErrorMessage } from "@shared/components/FormErrorMessage";
import { useTheme } from "@shared/theme/useTheme";
import { Form } from "@shared/components/Form";
import { UserInfoSummary } from "../UserInfoSummary";
import { Controller, useForm } from "react-hook-form";
import {
  SubscriptionCheckoutFormSchema,
  subscriptionCheckoutFormSchema,
} from "./subscription-checkout-form-helpers";
import { yupResolver } from "@hookform/resolvers/yup";
import { TextField } from "@shared/components/TextField";
import { CountrySelect } from "@shared/components/CountrySelect";
import { StateField } from "@shared/components/StateField";
import { getBrowserDefaultCountry } from "@shared/utils/countries";
import { TERMS_OF_SERVICE_AND_PRIVACY_POLICY_URL } from "@shared/utils/constants";
import { SubscriptionSubmitButton } from "../styles";
import { SubscriptionStepHeader } from "../SubscriptionStepHeader";

const PaymentMethodInputWrapper = styled.div<{ error: boolean }>`
  width: 100%;
  border-radius: 0.5rem;
  border: 1px solid
    ${(p) => (p.error ? p.theme.colors.error : p.theme.colors.border)};
  padding: 14.5px 16px;
  background-color: ${(p) => p.theme.colors.background};
  transition: border-color 0.2s ease-in-out;
`;

const ActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;

  ${(p) => p.theme.media.mobileDown} {
    flex-direction: column;
    align-items: flex-start;
    margin-top: 0;

    div {
      margin-top: 1rem;
      button {
        width: 100%;
      }
    }
  }
`;

const TermsText = styled(Typography)`
  a {
    color: ${(p) => p.theme.text.colors.primary};
    text-decoration: underline;
    &:hover {
      opacity: 0.8;
    }
  }
`;

type CheckoutFormProps = {
  defaultValues: {
    email: string;
    mlsId: string;
    name: string;
    phoneNumber: string;
    officeName: string;
    address: {
      state: string;
      line1: string;
      line2: string;
      city: string;
      zipCode: string;
      country: string;
    };
  };
  title?: string;
  hideUserInfoSummary?: boolean;
  subscriptionId: string;
  subscriptionInfo: {
    name: string;
    price: number;
    clientSecret?: string;
    currency?: string;
    availableOffer?: {
      id: string;
      trialDays?: number;
      validUntil?: Date;
      price?: number;
    };
  };
  lastPaymentFailedReason?: string;
  onSubmit: () => void;
  onBackClick: () => void;
};

export const SubscriptionCheckoutForm = (props: CheckoutFormProps) => {
  const {
    defaultValues,
    subscriptionInfo,
    subscriptionId,
    onSubmit,
    onBackClick,
    title,
    hideUserInfoSummary,
  } = props;
  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    getValues,
    control,
    watch,
  } = useForm<SubscriptionCheckoutFormSchema>({
    defaultValues: {
      addressLine1: defaultValues.address.line1,
      addressLine2: defaultValues.address.line2,
      addressCity: defaultValues.address.city,
      addressState: defaultValues.address.state,
      addressCountry:
        defaultValues.address.country || getBrowserDefaultCountry(),
      addressZipCode: defaultValues.address.zipCode,
    },
    resolver: yupResolver(subscriptionCheckoutFormSchema),
    reValidateMode: "onChange",
  });
  const selectedCountry = watch("addressCountry");
  const theme = useTheme("light");
  const [paymentErrorMessage, setPaymentErrorMessage] = useState("");
  const [paymentStatus, setPaymentStatus] = useState<
    "idle" | "loading" | "error"
  >("idle");
  const subscriptionFlowConfirmationMutation =
    useSubscriptionFlowConfirmation();
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    setTimeout(() => {
      const cardElement = elements?.getElement(CardElement);
      if (!cardElement) return;

      cardElement.on("change", ({ error }) => {
        setPaymentErrorMessage(error ? error.message : "");
      });
    }, 500);
  }, [elements, setValue, getValues]);

  if (!stripe || !elements) {
    return <Skeleton variant="rounded" sx={{ width: "100%", height: 480 }} />;
  }

  // A previous attempt may have already confirmed the SetupIntent with Stripe
  // even though the flow overall failed afterwards (e.g. our own backend
  // rejected the address). Stripe refuses to confirm an already-succeeded
  // SetupIntent again, so retrying must reuse it instead of re-confirming.
  const confirmOrRecoverSetupIntent = async (
    data: SubscriptionCheckoutFormSchema,
  ) => {
    const { setupIntent: existingSetupIntent } =
      await stripe.retrieveSetupIntent(subscriptionInfo.clientSecret!);

    if (existingSetupIntent?.status === "succeeded") {
      return { setupIntent: existingSetupIntent, error: undefined };
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) return { setupIntent: undefined, error: undefined };

    return stripe.confirmCardSetup(subscriptionInfo.clientSecret!, {
      payment_method: {
        card: cardElement,
        billing_details: {
          email: defaultValues.email,
          name: defaultValues.name,
          phone: defaultValues.phoneNumber ?? undefined,
          address: {
            line1: data.addressLine1,
            line2: data.addressLine2 ?? undefined,
            state: data.addressState,
            city: data.addressCity,
            postal_code: data.addressZipCode,
            country: data.addressCountry,
          },
        },
      },
    });
  };

  // When the subscribe-form is submitted we do a few things:
  // 1. Tokenize the payment method
  // 2. Create the subscription
  // 3. Handle any next actions like 3D Secure that are required for SCA.
  const onFormSubmit = async (data: SubscriptionCheckoutFormSchema) => {
    setPaymentStatus("loading");

    const { error, setupIntent } = await confirmOrRecoverSetupIntent(data);

    if (error && error.message) {
      setPaymentStatus("error");
      setPaymentErrorMessage(error.message);
      return;
    }

    if (setupIntent?.status === "succeeded") {
      try {
        await subscriptionFlowConfirmationMutation.mutateAsync({
          subscriptionId,
          address: {
            line1: data.addressLine1,
            line2: data.addressLine2 ?? null,
            state: data.addressState,
            city: data.addressCity,
            zipCode: data.addressZipCode,
            country: data.addressCountry,
          },
        });
      } catch {
        setPaymentStatus("idle");
        return;
      }
      onSubmit();
      return;
    }

    if (setupIntent?.status === "requires_payment_method") {
      setPaymentErrorMessage(
        "We are sorry, there was an error setting up your payment method. Please try again with a different payment method.",
      );
      setPaymentStatus("idle");
      return;
    }

    if (!setupIntent?.status || setupIntent?.status === "canceled") {
      setPaymentErrorMessage(
        "Something went wrong. Please try again or contact support if the issue persists.",
      );
    }

    setPaymentStatus("idle");
  };

  return (
    <div>
      <SubscriptionStepHeader
        onBack={onBackClick}
        backDisabled={paymentStatus === "loading"}
        marginBottom="1.5rem"
      >
        {!!title && <Typography variant="h6">{title}</Typography>}
      </SubscriptionStepHeader>
      {!hideUserInfoSummary && (
        <UserInfoSummary
          name={defaultValues.name}
          email={defaultValues.email}
          mlsId={defaultValues.mlsId}
          phoneNumber={defaultValues.phoneNumber}
          officeName={defaultValues.officeName}
        />
      )}
      <Form
        onSubmit={handleSubmit(onFormSubmit)}
        aria-disabled={paymentStatus === "loading"}
      >
        <FormControl>
          <FormLabel>Card Information</FormLabel>
          <PaymentMethodInputWrapper error={!!paymentErrorMessage}>
            <CardElement
              id="card-element"
              options={{
                style: {
                  base: {
                    color: theme.input.colors.primary,
                    fontFamily: theme.text.fonts.primary,
                  },
                  invalid: {
                    color: theme.colors.error,
                    iconColor: theme.colors.error,
                  },
                },
                hidePostalCode: true,
                disableLink: true,
              }}
            />
          </PaymentMethodInputWrapper>
          <FormErrorMessage>{paymentErrorMessage}</FormErrorMessage>
          <Typography mt={1}>
            <PaymentMethodsIcon />
          </Typography>
        </FormControl>

        <Grid container columnSpacing={2} rowSpacing={1}>
          <Grid item sm={6} xs={12}>
            <TextField
              label="Address"
              name="addressLine1"
              inputProps={{ ...register("addressLine1") }}
              fieldError={errors.addressLine1}
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <TextField
              label="Address line 2"
              name="addressLine2"
              inputProps={{ ...register("addressLine2") }}
              fieldError={errors.addressLine2}
            />
          </Grid>

          <Grid item sm={6} xs={12}>
            <TextField
              label="City"
              name="addressCity"
              inputProps={{ ...register("addressCity") }}
              fieldError={errors.addressCity}
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <Controller
              name="addressState"
              control={control}
              render={({ field }) => (
                <StateField
                  country={selectedCountry}
                  name="addressState"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  fieldError={errors.addressState}
                />
              )}
            />
          </Grid>

          <Grid item sm={6} xs={12}>
            <TextField
              label="Postal code"
              name="addressZipCode"
              inputProps={{ ...register("addressZipCode") }}
              fieldError={errors.addressZipCode}
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <Controller
              name="addressCountry"
              control={control}
              render={({ field }) => (
                <CountrySelect
                  label="Country"
                  name="addressCountry"
                  value={field.value}
                  onChange={(newCountry) => {
                    field.onChange(newCountry);
                    setValue("addressState", "");
                  }}
                  onBlur={field.onBlur}
                  fieldError={errors.addressCountry}
                />
              )}
            />
          </Grid>
        </Grid>

        <FormControl>
          <TermsText variant="body2">
            By clicking Start My Subscription, you agree to our{" "}
            <a
              href={TERMS_OF_SERVICE_AND_PRIVACY_POLICY_URL}
              target="_blank"
              rel="noreferrer"
            >
              Terms of Service and Privacy Policy.
            </a>
          </TermsText>
        </FormControl>

        <ActionsContainer>
          <Grid container justifyContent={"flex-end"} columnGap={2} mt={3}>
            <SubscriptionSubmitButton
              isLoading={paymentStatus === "loading"}
              loadingText="Processing..."
            >
              Start My Subscription
            </SubscriptionSubmitButton>
          </Grid>
        </ActionsContainer>
      </Form>
    </div>
  );
};
